import type { Bundle } from '../api'

/**
 * A session kept on the device (offline-first sessions, 9 Oct 2026).
 *
 * The whole session (`Bundle`) and how far the child has got. The server's
 * cursor only moves when an answer reaches it, so with answers waiting in
 * the outbox the device is ahead; carrying on starts from whichever is
 * further. Only the last few sessions are kept.
 */

const PREFIX = 'ws-session-v1:'
const KEEP = 4

interface Stored { bundle: Bundle; cursor: number; at: number }

function get(sessionId: string): Stored | null {
  try {
    const raw = window.localStorage.getItem(PREFIX + sessionId)
    return raw ? (JSON.parse(raw) as Stored) : null
  } catch {
    return null
  }
}

function put(sessionId: string, value: Stored): void {
  try {
    window.localStorage.setItem(PREFIX + sessionId, JSON.stringify(value))
    // Tidy up: keep the newest few sessions only.
    const all: [string, number][] = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i)
      if (k?.startsWith(PREFIX)) {
        try { all.push([k, (JSON.parse(window.localStorage.getItem(k) ?? '{}') as Stored).at ?? 0]) } catch { all.push([k, 0]) }
      }
    }
    all.sort((a, b) => b[1] - a[1]).slice(KEEP).forEach(([k]) => window.localStorage.removeItem(k))
  } catch {
    // Storage blocked: the session still runs, it just cannot survive a reload.
  }
}

export const sessionStore = {
  bundle(sessionId: string): Bundle | null {
    return get(sessionId)?.bundle ?? null
  },
  saveBundle(bundle: Bundle): void {
    const old = get(bundle.session_id)
    put(bundle.session_id, { bundle, cursor: Math.max(old?.cursor ?? 0, bundle.cursor), at: Date.now() })
  },
  cursor(sessionId: string): number {
    return get(sessionId)?.cursor ?? 0
  },
  setCursor(sessionId: string, cursor: number): void {
    const old = get(sessionId)
    if (old) put(sessionId, { ...old, cursor: Math.max(old.cursor, cursor), at: Date.now() })
  },
  forget(sessionId: string): void {
    try { window.localStorage.removeItem(PREFIX + sessionId) } catch { /* nothing to forget */ }
  },
}
